import {isEmpty} from 'lodash';
import {Injectable} from '@angular/core';
import {UserUtil} from '../utils/UserUtil';
import {Subject, Subscription} from 'rxjs';
import {Keys, StorageService} from '../service/storage.service';
import {LoanApplication} from '../model/loan-application.model';
import {LoanApplicationService} from '../service/loan-application.service';
import {LoanApplications} from '../model/loan-applications.model';
import {AdvanceEmiPlanService} from '../service/advance-emi-plan.service';
import {AdvanceEmiPlan} from '../model/AdvanceEmiPlan';
import {PreApprovedLoanManualCreditAssessment} from '../model/eligibility-check/pre-approved-loan-manual-credit-assessment.model';
import {CreditAssessmentService} from '../service/credit-assessment.service';
import {LoanApplicationSource} from '../model/loan-application/loan-application-source.model';
import {MultipleLoanEligibilityCheckResponse} from '../model/eligibility-check/multiple-loan-eligibility-check.model';

@Injectable()
export class LoanApplicationInteractor {
    private static readonly ApplicationRefreshedEventEmitter: Subject<LoanApplication> = new Subject<LoanApplication>();
    private readonly userUtil: UserUtil;
    private readonly storageService: StorageService;
    private readonly loanApplicationService: LoanApplicationService;
    private advanceEmiPlanService: AdvanceEmiPlanService;
    private readonly creditAssessmentService: CreditAssessmentService;

    constructor(userUtil: UserUtil, storageService: StorageService, loanApplicationService: LoanApplicationService,
                advanceEmiPlanService: AdvanceEmiPlanService, creditAssessmentService: CreditAssessmentService) {
        this.userUtil = userUtil;
        this.storageService = storageService;
        this.loanApplicationService = loanApplicationService;
        this.advanceEmiPlanService = advanceEmiPlanService;
        this.creditAssessmentService = creditAssessmentService;
    }

    public async create(loanApplication: LoanApplication): Promise<LoanApplication> {
        const createdLoanApplication = await this.loanApplicationService.createLoanApplication(loanApplication);

        if (loanApplication.advanceEmiPlan) {
            const advanceEmiPlan = loanApplication.advanceEmiPlan;

            const advanceEmiPlanToSave = new AdvanceEmiPlan(advanceEmiPlan.requestedAmount, advanceEmiPlan.advanceEmiOption, createdLoanApplication.id);

            createdLoanApplication.advanceEmiPlan = await this.advanceEmiPlanService.create(advanceEmiPlanToSave);
        }

        return createdLoanApplication;
    }

    public async update(loanApplication: LoanApplication): Promise<LoanApplication> {
        await this.loanApplicationService.updateLoanApplication(loanApplication);
        return loanApplication;
    }

    public async get(): Promise<LoanApplication> {
        const localApplication = await this.storageService.getObject<LoanApplication>(Keys.LOAN_APPLICATION);
        if (this.userUtil.isUserLoggedIn() && !isEmpty(localApplication) && !isEmpty(localApplication.id)) {
            const applicationId = localApplication.id;
            const applicationFromService = await this.loanApplicationService.getLoanApplication(applicationId);
            this.saveApplicationToStorage(applicationFromService);
            return applicationFromService;
        } else {
            return localApplication;
        }
    }

    public async getByUserId(userId: string): Promise<LoanApplications> {
        return await this.loanApplicationService.getLoanApplicationsByUserId(userId);
    }

    public async registerForApplicationRefreshEvent(callbackReceiver: (application: LoanApplication) => any): Promise<Subscription> {
        return LoanApplicationInteractor.ApplicationRefreshedEventEmitter.subscribe((application) => callbackReceiver(application));
    }

    public saveApplicationToStorage(loanApplication: LoanApplication) {
        this.storageService.saveObject(Keys.LOAN_APPLICATION, loanApplication);
        LoanApplicationInteractor.ApplicationRefreshedEventEmitter.next(loanApplication);
    }

    public async canReapplyForLoan(userId: string): Promise<boolean> {
        return await this.loanApplicationService.canReapplyForLoan(userId);
    }

    public async getMultipleLoanEligibilityForUser(userId: string, source?: LoanApplicationSource): Promise<MultipleLoanEligibilityCheckResponse> {
        return await this.loanApplicationService.getMultipleLoanEligibilityForUser(userId, source);
    }

    public async getAllPreApprovedLoanOffersForUser(userId: string): Promise<PreApprovedLoanManualCreditAssessment[]> {
        return await this.creditAssessmentService.getPreApprovedLoanOffersForUser(userId, true);
    }

    public async getActivePreApprovedLoanOfferForUser(userId: string): Promise<PreApprovedLoanManualCreditAssessment> {
        const offers = await this.creditAssessmentService.getPreApprovedLoanOffersForUser(userId, false);
        return offers && offers.length > 0 ? offers[0] : null;
    }
}
